import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearch } from 'wouter';
import type { PortalCycleSummary } from '@shared/portal-api-types';
import {
  PORTAL_CYCLE_QUERY_KEY,
  buildPortalHref,
  parsePortalCycleId,
  resolvePortalCycleSelection,
} from './portalCycleQuery';

export interface UsePortalCycleSelectionResult {
  selectedCycleId: number | null;
  onCycleChange: (cycleId: number) => void;
}

export function usePortalCycleSelection(
  cycles: PortalCycleSummary[] | undefined,
): UsePortalCycleSelectionResult {
  const [pathname, setLocation] = useLocation();
  const search = useSearch();
  const syncedCycleListRef = useRef<string>('');

  const urlCycleId = useMemo(() => parsePortalCycleId(search), [search]);

  const selectedCycleId = useMemo(() => {
    if (!cycles?.length) return urlCycleId;
    return resolvePortalCycleSelection(urlCycleId, cycles).cycleId;
  }, [cycles, urlCycleId]);

  const writeCycleToUrl = useCallback((
    cycleId: number,
    options?: { replace?: boolean; extraUpdates?: Record<string, null | undefined> },
  ) => {
    const updates: Record<string, number | null | undefined> = {
      [PORTAL_CYCLE_QUERY_KEY]: cycleId,
      ...options?.extraUpdates,
    };
    setLocation(buildPortalHref(pathname, search, updates), { replace: options?.replace ?? false });
  }, [pathname, search, setLocation]);

  useEffect(() => {
    if (!cycles?.length) return;

    const cycleKey = cycles.map((cycle) => cycle.id).join(',');
    const resolution = resolvePortalCycleSelection(urlCycleId, cycles);
    const alreadySynced = syncedCycleListRef.current === `${cycleKey}:${urlCycleId}:${resolution.replaceUrl}`;

    if (resolution.replaceUrl && resolution.cycleId != null && !alreadySynced) {
      syncedCycleListRef.current = `${cycleKey}:${urlCycleId}:${resolution.replaceUrl}`;
      writeCycleToUrl(resolution.cycleId, { replace: true });
      return;
    }

    syncedCycleListRef.current = `${cycleKey}:${urlCycleId}:${resolution.replaceUrl}`;
  }, [cycles, urlCycleId, writeCycleToUrl]);

  const onCycleChange = useCallback((cycleId: number) => {
    writeCycleToUrl(cycleId, {
      replace: false,
      extraUpdates: { campaignId: null },
    });
  }, [writeCycleToUrl]);

  return { selectedCycleId, onCycleChange };
}
